import { Box, Button, Paper, TableContainer, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chip from '../../../components/Chip/Chip';
import DefaultTable from '../../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../../components/DefaultTable/TableToolbar/TableToolbar';
import GlobalSearch from '../../../components/GlobalSearch/GlobalSearch';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { CompanyStatus } from '../../../enums/companyStatus';
import { useShowFilterOrSearch } from '../../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../../hooks/useTableFunctions';
import { Company } from '../../../models/company';
import { HeadCell } from '../../../models/headCell';
import { Pagination } from '../../../models/pagination';
import {
  ADMINISTRATION_COMPANIES_CREATE,
  ADMINISTRATION_COMPANIES_EDIT
} from '../../../router/constants';
import { companyServices } from '../../../services/companyServices';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../../utils/helpers';
import { getRoundStatusStyle } from '../../../utils/statusRoundStyle';
import ActivateModal from './ActivateModal';
import DeactivateModal from './DeactivateModal';
import FilterCompanies, { FilterCompaniesForm } from './FilterCompanies';
import { FreelancerType } from '../../../enums/freelancerType';

const initialCompaniesColumnsData: HeadCell[] = [
  {
    id: 'id',
    label: 'Company ID',
    sortable: true,
    visible: true,
    render: (company: Company) => company.id
  },
  {
    id: 'name',
    label: 'Company Name',
    sortable: true,
    visible: true,
    render: (company: Company) => company.name
  },
  {
    id: 'freelancer',
    label: 'Freelancers',
    sortable: true,
    visible: true,
    thStyle: { width: 200, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (company: Company) => FreelancerType[company.freelancer]
  },
  {
    id: 'markup',
    label: 'Markup',
    sortable: true,
    visible: true,
    thStyle: { textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (company: Company) => company.markup + '%'
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    visible: true,
    thStyle: { width: 200, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (company: Company) => (
      <Box sx={getRoundStatusStyle(CompanyStatus[company.status])}>
        {CompanyStatus[company.status]}
      </Box>
    )
  }
];

const CompaniesList = (): JSX.Element => {
  const initialFilterData: FilterCompaniesForm = {
    companiesIds: [],
    markup: '',
    status: []
  };
  const [deactivateModalData, setDeactivateModalData] = useState<{
    open: boolean;
    company?: Company;
  }>({ open: false });
  const [activateModalData, setActivateModalData] = useState<{
    open: boolean;
    company?: Company;
  }>({ open: false });
  const [paginationCompanies, setPaginationCompanies] = useState<
    Pagination<Company> | undefined
  >(undefined);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterCompaniesForm>(
    initialCompaniesColumnsData,
    initialFilterData,
    'name'
  );

  useEffect(() => {
    getCompanies();
  }, [page, rowsPerPage, sortBy, sortType, filterData, searchTerm]);

  const getCompanies = () => {
    companyServices
      .getCompanies(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortType}`
        }
      })
      .then((response) => {
        setPaginationCompanies(response.data);
        setLoading(false);
      });
  };

  const onDeactivateCompany = (
    event: React.MouseEvent<unknown>,
    company: Company
  ) => {
    event.stopPropagation();
    setDeactivateModalData({ open: true, company });
  };

  const onActivateCompany = (
    event: React.MouseEvent<unknown>,
    company: Company
  ) => {
    event.stopPropagation();
    setActivateModalData({ open: true, company });
  };

  const onEditCompany = (
    event: React.MouseEvent<unknown>,
    company: Company
  ) => {
    event.stopPropagation();
    navigate(`${ADMINISTRATION_COMPANIES_EDIT}/${company.id}`);
  };

  const onShowEdit = () => true;

  const onShowActivate = (company: Company) => {
    return company.status === 'INACTIVE';
  };

  const onShowDeactivate = (company: Company) => {
    return company.status === 'ACTIVE';
  };

  const showCheckBox = () => true;

  const handleExport = () => {
    companyServices.exportCompanies(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'companies');
    });
  };

  const confirmUserActionStatus = (company: Company | undefined) => {
    if (company?.id) {
      companyServices
        .activateCompany({
          id: company.id,
          activate: company.status !== 'ACTIVE'
        })
        .then(() => {
          getCompanies();
          setDeactivateModalData({ open: false, company: undefined });
          setActivateModalData({ open: false, company: undefined });
        });
    }
  };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.companiesIds) {
      body = {
        ...body,
        companiesIds: filterData.companiesIds
          ? filterData.companiesIds.map((company) => company.id)
          : []
      };
    }
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }

    return body;
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <PageTitle>View All Companies</PageTitle>
        <Chip size={paginationCompanies?.totalElements} />
        <Box flexGrow={1} />
        <Button
          sx={{ mb: 3 }}
          onClick={() => navigate(ADMINISTRATION_COMPANIES_CREATE)}
        >
          Add New Company
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
        <TableToolbar
          viewTableColumnsProps={{
            columns: columnsData,
            onColumnClick: onChangeColumnVisibility
          }}
          onFilter={onFilterClick}
          onSearch={onSearchClick}
          onExport={handleExport}
        >
          {showSearch && (
            <GlobalSearch
              onSearchChange={onSearchChange}
              searchTerm={searchTerm}
            />
          )}
          {showFilters && (
            <FilterCompanies
              handleFormSubmit={onFilterChange}
              filterData={filterData}
            />
          )}
        </TableToolbar>
        <DefaultTable
          columns={columnsData}
          data={paginationCompanies?.content}
          rowClick={(company: Company) =>
            navigate(`${ADMINISTRATION_COMPANIES_EDIT}/${company.id}`)
          }
          sortProps={{ sortBy, sortType, onRequestSort }}
          checkBoxes={{
            showCheckBox
          }}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationCompanies?.totalElements
          }}
          actionProps={{
            onEdit: onEditCompany,
            onDeactivate: onDeactivateCompany,
            onActivate: onActivateCompany,
            showEditButton: onShowEdit,
            showActivateButton: onShowActivate,
            showDeactivateButton: onShowDeactivate,
            style: { padding: '11px' }
          }}
        />
        {!loading && !paginationCompanies?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No users found!
          </Typography>
        )}
      </TableContainer>
      <DeactivateModal
        modalData={deactivateModalData}
        closeModal={() => setDeactivateModalData({ open: false })}
        confirmModal={() =>
          confirmUserActionStatus(deactivateModalData.company)
        }
      />
      <ActivateModal
        modalData={activateModalData}
        closeModal={() => setActivateModalData({ open: false })}
        confirmModal={() => confirmUserActionStatus(activateModalData.company)}
      />
    </>
  );
};

export default CompaniesList;
